import React, { ChangeEvent, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { useMutation } from '@apollo/react-hooks';
import { utils } from 'kb-shared';
import { useTranslation } from 'react-i18next';
import Honeybadger from '@honeybadger-io/js';

import { ReactComponent as MessagesIcon } from '../../assets/icons/messaging.svg';
import { locker } from '../../assets/icons';
import { uploadFile, CATEGORY, OTHER_ID_TYPES } from '../../api/pf2/files';
import { MessageAttachmentUpload } from './MessageAttachmentUpload';
import {
  CreateMessageMutationResponse,
  CREATE_MESSAGE_MUTATION
} from './graphql/createMessageMutation';
import {
  Bubble,
  Container,
  ErrorContainer,
  Icon,
  LockedInput,
  RemainingChars,
  SendButton,
  Textarea,
  WhiteInput
} from './styled';
import { DocumentNode } from 'graphql';

const { getLabInfo } = utils;

interface Props {
  conversationId: string;
  afterSend: () => void;
  status: string;
}

const DELAY_ON_SEND = 300;
const MAX_CHARS = 1000;

const MessageInput = ({ conversationId, afterSend, status }: Props) => {
  const { t: translate } = useTranslation();
  const [message, setMessage] = useState<string>('');
  const [remainingChars, setRemainingChars] = useState<number>(MAX_CHARS);
  const [error, setError] = useState<string>('');
  const [listOfAttachments, setListOfAttachments] = useState<File[]>([]);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [sendReply] = useMutation<CreateMessageMutationResponse>(
    CREATE_MESSAGE_MUTATION as DocumentNode
  );

  const { number } = getLabInfo('NONE', process.env.REACT_APP_ENV);

  const isSolved = status === 'resolved';

  function onDeleteAttachment(idx: number) {
    listOfAttachments.splice(idx, 1);
    setListOfAttachments([...listOfAttachments]);
  }

  function uploadAllFiles(messageId: number): Promise<unknown> {
    const promiseUploadFiles = listOfAttachments.map(file =>
      uploadFile(file, CATEGORY.MESSAGE_ATTACHMENT, messageId, OTHER_ID_TYPES.MESSAGE_ID)
    );

    return Promise.all(promiseUploadFiles);
  }

  function clearAllInputs() {
    setListOfAttachments([]);
    setMessage('');
  }

  const onSendMessage = debounce(async () => {
    const text = textAreaRef?.current?.value || '';

    try {
      if (text.trim().length === 0) {
        throw 'Please enter a message.';
      }

      const { data } = await sendReply({
        variables: {
          conversationId: parseInt(conversationId),
          body: text
        }
      });

      if (!data) {
        throw 'Please try again.';
      }

      const { message } = data.createMessage;

      await uploadAllFiles(message.id);

      afterSend();
      setError('');
      clearAllInputs();
    } catch (error) {
      Honeybadger.notify(error);
      setError(error);
    }
  }, DELAY_ON_SEND);

  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (remainingChars < 0) {
      return;
    }

    const value = event.target.value.slice(0, MAX_CHARS);

    setMessage(value);
    setRemainingChars(MAX_CHARS - value.length);
  };

  return (
    <Container>
      <Bubble>
        <MessagesIcon />
        <span>
          {translate('messages.new_message.reply_hint')} <a href={`tel:${number}`}>call us</a>.
        </span>
      </Bubble>

      {isSolved ? (
        <LockedInput>
          <Icon src={locker} />
          <span>
            A member of the Kindbody team has closed this conversation. Please send us a new message
            if you have another issue or question.
          </span>
        </LockedInput>
      ) : (
        <>
          <WhiteInput
            onClick={() => {
              if (textAreaRef?.current) {
                textAreaRef?.current.focus();
              }
            }}
          >
            <Textarea
              inputRef={textAreaRef}
              value={message}
              onChange={handleOnChange}
              maxLength={MAX_CHARS}
            />
            <SendButton onClick={onSendMessage}>Send</SendButton>
          </WhiteInput>
          <RemainingChars>
            {translate('messages.input.remaining_chars', { count: remainingChars })}
          </RemainingChars>
        </>
      )}

      {error && (
        <ErrorContainer className="error">
          <b>Error:</b> {error}
        </ErrorContainer>
      )}

      {!isSolved && (
        <MessageAttachmentUpload
          listOfAttachments={listOfAttachments}
          setListOfAttachments={setListOfAttachments}
          onDeleteAttachment={onDeleteAttachment}
        />
      )}
    </Container>
  );
};

export default MessageInput;
